import React from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import MaskedReveal from "./MaskedReveal";
import HeroWordMarquee from "./HeroWordMarquee";
import HeroAppPreview from "./HeroAppPreview";
import HeroBannerCarousel from "./HeroBannerCarousel";
import googlePlayIcon from "../images/Join_Now_GooglePlay.png";
import appStoreIcon from "../images/Download_AppStore.png";

const GOOGLE_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.deepsoul.app.ai";
const APP_STORE_URL =
  "https://apps.apple.com/hk/app/deep-soul-spiritual-heritage/id6748895978?l=en-GB";

const Hero: React.FC = () => {
  const { t, i18n } = useTranslation();
  const shouldReduceMotion = useReducedMotion();
  const isEnglish = String(i18n.language).toLowerCase().startsWith("en");

  return (
    <Box
      as="section"
      aria-labelledby="hero-title"
      position="relative"
      width="100%"
      minH={{ base: "auto", lg: "calc(100svh - 72px)" }}
      overflow="hidden"
      isolation="isolate"
      bg="linear-gradient(160deg, rgba(217, 238, 251, 0.96) 0%, rgba(238, 240, 255, 0.88) 42%, rgba(255, 190, 184, 0.86) 100%)"
      _after={{
        content: '""',
        position: "absolute",
        inset: "auto 0 0",
        height: { base: "90px", md: "150px" },
        bg: "linear-gradient(180deg, rgba(244, 245, 247, 0), #F4F5F7)",
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      <Box
        aria-hidden="true"
        position="absolute"
        top={{ base: "-90px", md: "-140px" }}
        right={{ base: "-150px", md: "6%" }}
        width={{ base: "340px", md: "520px" }}
        height={{ base: "340px", md: "520px" }}
        borderRadius="full"
        bg="radial-gradient(circle, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 68%)"
        pointerEvents="none"
      />

      <HeroWordMarquee />

      <SimpleGrid
        columns={{ base: 1, lg: 2 }}
        spacing={{ base: 8, md: 12, lg: 14 }}
        alignItems="center"
        position="relative"
        zIndex={1}
        width="min(1180px, calc(100% - 32px))"
        minH={{ base: "auto", lg: "calc(100svh - 72px)" }}
        mx="auto"
        pt={{ base: 8, md: 16, lg: 12 }}
        pb={{ base: 20, md: 28, lg: 24 }}
      >
        <Flex
          data-testid="hero-copy"
          direction="column"
          align={{ base: "center", lg: "flex-start" }}
        >
          <MaskedReveal>
            <Flex
              display="inline-flex"
              align="center"
              gap={3}
              mb={{ base: 4, md: 5 }}
              maxW="calc(100vw - 32px)"
              px={{ base: 6, md: 8 }}
              py={{ base: 3.5, md: 4 }}
              border="1px solid rgba(255, 255, 255, 0.72)"
              borderRadius="full"
              bg="rgba(255, 255, 255, 0.68)"
              boxShadow="0 8px 26px rgba(80, 82, 92, 0.08)"
              color="#EF2F3F"
              fontSize={{
                base: "clamp(0.875rem, 4.7vw, 1.125rem)",
                md: "xl",
              }}
              fontWeight="800"
            >
              <Text as="span" whiteSpace="nowrap">
                {t("hero.badge")}
              </Text>
            </Flex>
          </MaskedReveal>

          <MaskedReveal delay={0.1}>
            <Heading
              as="h1"
              id="hero-title"
              maxW="760px"
              mb={{ base: 4, md: 6 }}
              color="#25272D"
              fontSize={
                isEnglish
                  ? {
                      base: "clamp(2.15rem, 9.5vw, 3.35rem)",
                      md: "4xl",
                      lg: "clamp(3.2rem, 4.3vw, 4.45rem)",
                    }
                  : {
                      base: "clamp(2.25rem, 10.5vw, 3.6rem)",
                      md: "5xl",
                      lg: "clamp(3.9rem, 5.7vw, 5.8rem)",
                    }
              }
              lineHeight={{ base: 1.04, lg: isEnglish ? 1.02 : 0.99 }}
              letterSpacing={isEnglish ? "-0.045em" : "-0.055em"}
              fontWeight="900"
              textAlign={{ base: "center", lg: "left" }}
            >
              {t("hero.title")}
            </Heading>
          </MaskedReveal>

          <MaskedReveal delay={0.2}>
            <Text
              maxW="660px"
              mb={{ base: 6, md: 8 }}
              color="rgba(37, 39, 45, 0.72)"
              fontSize={{ base: "md", md: "xl" }}
              lineHeight={{ base: 1.75, md: 1.82 }}
              textAlign={{ base: "center", lg: "left" }}
            >
              {t("hero.description")}
            </Text>
          </MaskedReveal>

          <MaskedReveal delay={0.25}>
            <Text
              data-testid="hero-brand-lockup"
              mb={{ base: 6, md: 7 }}
              color="#25272D"
              fontSize={
                isEnglish
                  ? { base: "lg", md: "xl", lg: "2xl" }
                  : { base: "2xl", md: "3xl", lg: "4xl" }
              }
              fontWeight="900"
              letterSpacing={isEnglish ? "0" : "0.04em"}
              textAlign={{ base: "center", lg: "left" }}
            >
              {t("hero.subtitlePrimary")} × {t("hero.subtitleSecondary")}
            </Text>
          </MaskedReveal>

          <MaskedReveal delay={0.3}>
            <Flex
              direction="column"
              align={{ base: "stretch", sm: "center", lg: "flex-start" }}
              width={{ base: "100%", sm: "auto" }}
            >
              <Box
                id="hero-downloads"
                role="region"
                aria-label={t("hero.download.pickerLabel")}
                maxW="100%"
                px={{ base: 4, md: 6 }}
                py={{ base: 4, md: 5 }}
                border="1px solid rgba(255, 255, 255, 0.78)"
                borderRadius="22px"
                bg="rgba(255, 255, 255, 0.68)"
                boxShadow="0 14px 34px rgba(80, 82, 92, 0.1)"
                backdropFilter="blur(14px)"
              >
                <Flex align="center" justify="center" wrap="wrap" gap={{ base: 3, md: 4 }}>
                  <Button
                    as="a"
                    href={GOOGLE_PLAY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="unstyled"
                    flex="0 0 auto"
                    height="auto"
                    width="auto"
                    aria-label={t("hero.download.googlePlayAlt")}
                    transition="transform 180ms ease"
                    _hover={{ transform: "translateY(-2px)" }}
                    _focusVisible={{
                      outline: "3px solid rgba(37, 39, 45, 0.72)",
                      outlineOffset: "3px",
                      borderRadius: "12px",
                    }}
                  >
                    <Image
                      src={googlePlayIcon}
                      alt=""
                      width={{ base: "132px", sm: "150px", md: "156px" }}
                      height={{ base: "44px", sm: "50px", md: "52px" }}
                      objectFit="contain"
                      borderRadius="10px"
                    />
                  </Button>
                  <Button
                    as="a"
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="unstyled"
                    flex="0 0 auto"
                    height="auto"
                    width="auto"
                    aria-label={t("hero.download.appStoreAlt")}
                    transition="transform 180ms ease"
                    _hover={{ transform: "translateY(-2px)" }}
                    _focusVisible={{
                      outline: "3px solid rgba(37, 39, 45, 0.72)",
                      outlineOffset: "3px",
                      borderRadius: "12px",
                    }}
                  >
                    <Image
                      src={appStoreIcon}
                      alt=""
                      width={{ base: "132px", sm: "150px", md: "156px" }}
                      height={{ base: "44px", sm: "50px", md: "52px" }}
                      objectFit="contain"
                      borderRadius="10px"
                    />
                  </Button>
                </Flex>
              </Box>
            </Flex>
          </MaskedReveal>
        </Flex>

        <MaskedReveal delay={0.18}>
          <Box position="relative" width="100%" maxW={{ base: "660px", lg: "none" }} mx="auto">
            <motion.div
              aria-hidden="true"
              style={{
                position: "absolute",
                width: "132px",
                height: "132px",
                right: "-34px",
                top: "52px",
                borderRadius: "999px",
                background: "rgba(255, 63, 79, 0.16)",
              }}
              animate={
                shouldReduceMotion
                  ? undefined
                  : { y: [0, -18, 0], scale: [1, 1.04, 1] }
              }
              transition={{ duration: 7, repeat: shouldReduceMotion ? 0 : Infinity, ease: "easeInOut" }}
            />
            <motion.div
              aria-hidden="true"
              style={{
                position: "absolute",
                width: "84px",
                height: "84px",
                left: "-28px",
                bottom: "72px",
                borderRadius: "999px",
                background: "rgba(217, 238, 251, 0.82)",
              }}
              animate={shouldReduceMotion ? undefined : { y: [0, 16, 0] }}
              transition={{ duration: 8.5, repeat: shouldReduceMotion ? 0 : Infinity, ease: "easeInOut" }}
            />

            <Box
              data-testid="hero-showcase-card"
              position="relative"
            >
              <Box data-testid="hero-banner" width="100%" mx="auto">
                <HeroBannerCarousel />
              </Box>

            </Box>
          </Box>
        </MaskedReveal>

        <Box gridColumn={{ lg: "1 / -1" }} width="100%">
          <MaskedReveal delay={0.3}>
            <Box>
              <HeroAppPreview />
            </Box>
          </MaskedReveal>
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default Hero;
